import { GamePack, GamePackIcon, GamePackPalette, GamePackStatus } from '@/API';
import { createSession } from '@/client/session';
import GamePackSelectionCard from '@/components/GamePacks/GamePackSelectionCard';
import AddPromptModal from '@/components/GamePacks/Prompts/AddPrompt';
import { useAuth } from '@/providers/AuthProvider';
import { defaultGamePack, gamePackIconOptions, listGamePackHost } from '@/utils/gamePacks';
import { createPrompt as apiCreatePrompt } from '@/client/gamePacks';
import { DropResult } from '@hello-pangea/dnd';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PromptList, { PromptExtended } from '@/components/GamePacks/Prompts/PromptList';
import ChevronSmLeftIcon from '@/components/Icons/ChevronSmLeftIcon';
import Background from '@/components/Host/Setup/Background';
import ContentBackground from '@/components/Host/Setup/ContentBackground';
import CustomPrompt from '@/components/GamePacks/Prompts/CustomPrompt';
import GameSidebar from '@/components/Host/Setup/GameSidebar';
import { LayoutContext } from '@/components/Navigation/LayoutContext';
import ReactGA from 'react-ga4';
import FreeTrialBanner from '@/components/FreeTrial/FreeTrialBanner';

// Maximum number of prompts that can be selected
export const MAX_SELECTED_PROMPTS = 2;

const customGamePack: GamePack = {
  id: 'custom',
  name: 'Custom Prompts',
  description: 'Personalize the game with your own prompts',
  icon: GamePackIcon.PENCIL,
  palette: GamePackPalette.LIGHT_GREEN,
  status: GamePackStatus.ACTIVE,
  createdAt: '',
  updatedAt: '',
  isFeatured: false,
  isNew: false,
  showInDiscover: false,
  order: 0,
  __typename: 'GamePack'
};

// Main component
export default function GameSetup() {
  // Data
  const [gamePacks, setGamePacks] = useState<GamePack[]>([customGamePack]);
  const [availablePrompts, setAvailablePrompts] = useState<PromptExtended[]>([]);

  // State
  const [selectedPrompts, setSelectedPrompts] = useState<PromptExtended[]>([]);
  const [activeGamePack, setActiveGamePack] = useState<GamePack | null>({} as GamePack);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [gamePacksLoaded, setGamePacksLoaded] = useState(false);

  // Modals
  const [addPromptModal, setAddPromptModal] = useState<boolean>(false);

  // Hooks
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams] = useSearchParams();

  const { setNavbarCollapsed } = useContext(LayoutContext);

  const tenantId = auth.groups?.find((group) => group.startsWith('tenant:'))?.split(':')[1];

  useEffect(() => {
    setNavbarCollapsed(true);
    const fetchData = async () => {
      if (!tenantId) return;
      const response = await listGamePackHost();
      const selectedGamePack = searchParams.get('gamepack');

      const gamePackList = response.data.gamePackList;

      gamePackList.unshift(customGamePack);

      setGamePacks(gamePackList);

      if (selectedGamePack) {
        setActiveGamePack(
          gamePackList.find((gamePack) => gamePack.id === selectedGamePack) ?? gamePackList[0]
        );
      } else {
        setActiveGamePack(
          gamePackList.find((gamePack) => gamePack.name === defaultGamePack) ?? gamePackList[0]
        );
      }

      setGamePacksLoaded(true);
    };

    fetchData();
  }, [tenantId]);

  useEffect(() => {
    const newAvailablePrompts = gamePacks
      .filter((gamePack) => activeGamePack?.id === gamePack.id)
      .flatMap((gamePack) =>
        (gamePack.prompts ?? []).map((item) => ({
          ...item,
          gamePack: gamePack
        }))
      );
    setAvailablePrompts(newAvailablePrompts as PromptExtended[]);
  }, [activeGamePack, gamePacks, selectedPrompts]);

  /*================================ EVENTS ==============================*/

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    // Condition: Dragging within the same list
    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === 'selectedPrompts') {
        const result = Array.from(selectedPrompts);
        const [removed] = result.splice(source.index, 1);
        result.splice(destination.index, 0, removed);

        setSelectedPrompts(result);
      }
      return;
    }

    // Condition: Dragging between lists
    if (
      source.droppableId === 'availablePrompts' &&
      destination.droppableId === 'selectedPrompts'
    ) {
      // Check if adding would exceed the maximum
      if (selectedPrompts.length >= MAX_SELECTED_PROMPTS) {
        setError(`You can only select up to ${MAX_SELECTED_PROMPTS} prompts`);
        return;
      }

      const [movedPrompt] = availablePrompts.splice(source.index, 1);
      setSelectedPrompts((prevSelected) => {
        const newSelected = [...prevSelected];
        newSelected.splice(destination.index, 0, movedPrompt);
        return newSelected;
      });
      setAvailablePrompts([...availablePrompts]);
      setError(null); // Clear any existing errors
    } else if (
      source.droppableId === 'selectedPrompts' &&
      destination.droppableId === 'availablePrompts'
    ) {
      const [movedPrompt] = selectedPrompts.splice(source.index, 1);
      setSelectedPrompts([...selectedPrompts]);
      setAvailablePrompts((prevAvailable) => {
        const newAvailable = [...prevAvailable];
        newAvailable.splice(destination.index, 0, movedPrompt);
        return newAvailable;
      });
      setError(null); // Clear any existing errors when removing prompts
    }
  };

  /*================================ Handlers ==============================*/

  const handleAddCustomPrompt = async (prompt: string) => {
    // Check if adding would exceed the maximum
    if (selectedPrompts.length >= MAX_SELECTED_PROMPTS) {
      setError(`You can only select up to ${MAX_SELECTED_PROMPTS} prompts`);
      setAddPromptModal(false);
      return;
    }

    const customPromptError =
      'Unable to create custom prompt. Please try again or contact an admin.';

    try {
      const result = await apiCreatePrompt(prompt);
      const created = (result as any)?.data?.promptCreate;

      if (!created?.id) {
        setError(customPromptError);
        return;
      }

      setSelectedPrompts((prev) => [...prev, { id: created.id, text: created.text } as any]);
      setError(null);
    } catch {
      setError(customPromptError);
    } finally {
      setAddPromptModal(false);
    }
  };

  const handleClear = () => {
    setSelectedPrompts([]);
    setError(null);
  };

  /**
   * Launch game with selected game pack and prompts.
   * @returns {Void} Returns void when complete.
   */
  const launchGame = async () => {
    if (selectedPrompts.length > 0) {
      setLoading(true);
      ReactGA.event({
        category: 'Create a Game',
        action: 'Clicked Button',
        label: 'Start Game'
      });
      try {
        const result = await createSession({
          promptIds: selectedPrompts.map((p) => p.id)
        });
        navigate(`/session/${result?.id}/host`);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {/* <AlertComponent /> */}
      <div className="w-0 h-0 overflow-hidden" aria-hidden="true">
        {gamePackIconOptions.map((icon) => (
          <div key={icon.label}>
            <img src={icon.icons.small} />
            <img src={icon.icons.big} />
          </div>
        ))}
      </div>

      <div className="w-full h-full flex relative">
        {activeGamePack && (
          <Background
            gamepackPalette={activeGamePack.palette}
            updateKey={activeGamePack.name}
            loading={!gamePacksLoaded}
          />
        )}
        <div className="ml-auto mr-auto h-full grid content-start z-10 relative min-w-920 @8xl:min-w-1120">
          {activeGamePack && (
            <ContentBackground
              gamePackIcon={activeGamePack.icon}
              updateKey={activeGamePack.name}
              loading={!gamePacksLoaded}
            />
          )}
          <div className="py-10 border-b border-neutral-200 z-10 flex justify-between items-center">
            <Link to="/dashboard" className="link link-game py-9">
              <ChevronSmLeftIcon />
              <span>Back To Home</span>
            </Link>
            <FreeTrialBanner smallVersion />
          </div>
          {gamePacksLoaded && (
            <div className="pt-56 min-h-0 flex flex-col w-max h-full z-10">
              <h2 className="text-h2 text-neutral-500 mb-4">Create a Game</h2>
              <p className="text-body text-neutral-500 mb-56">
                Explore prompt packs and pick the best match for your team’s focus today.
              </p>
              <div className="flex gap-0 min-h-0 items-start">
                <div className="h-full overflow-y-auto overflow-x-hidden w-398 -ml-100 pl-100 pr-100 -mr-68 shrink-0 pb-48 scrollbar-hidden">
                  <div className="w-198 rounded-xs bg-white py-12 flex flex-col items-center">
                    {gamePacks.map((gamePack) => (
                      <GamePackSelectionCard
                        gamePack={gamePack}
                        onClick={() => setActiveGamePack(gamePack)}
                        selected={activeGamePack?.id === gamePack.id}
                        key={gamePack.id}
                      />
                    ))}
                  </div>
                </div>
                {activeGamePack && (
                  <div className="h-full w-350 mr-10 shrink-0 pb-48 overflow-x-auto z-10 @8xl:w-550">
                    <div className="w-328 @8xl:w-528">
                      {activeGamePack.id === 'custom' ? (
                        <CustomPrompt
                          onClick={() => setAddPromptModal(true)}
                          disabled={selectedPrompts.length >= MAX_SELECTED_PROMPTS}
                        />
                      ) : (
                        <PromptList
                          title={activeGamePack.name}
                          prompts={availablePrompts}
                          selectedPrompts={selectedPrompts}
                          setSelectedPrompts={setSelectedPrompts}
                          disabled={selectedPrompts.length >= MAX_SELECTED_PROMPTS}
                        />
                      )}
                    </div>
                  </div>
                )}
                <div className="h-full shrink-0 pb-48 overflow-x-auto">
                  <GameSidebar
                    onDragEnd={onDragEnd}
                    clear={handleClear}
                    selectedPrompts={selectedPrompts}
                    setSelectedPrompts={setSelectedPrompts}
                    launchGame={launchGame}
                    loading={loading}
                    keepTip={false}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <span className="block sm:inline">{error}</span>
          <button
            className="float-right text-red-700 hover:text-red-900"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}

      <AddPromptModal
        isOpen={addPromptModal}
        onClose={() => setAddPromptModal(false)}
        onAddPrompt={handleAddCustomPrompt}
      />
    </>
  );
}
